// @ts-ignore
import Cookies from 'js-cookie';

import {PROJ_PFX} from '@/constants';

/**
 * Хранение выбранного языка.
 *
 * Язык всегда жил в cookie `lang_checked`, но виджет открывается в
 * кросс-доменном iframe, а Safari (ITP) молча отбрасывает запись third-party
 * cookie. Из-за этого `lang_checked` никогда не сохранялся, проверка
 * «язык профиля !== cookie» в DashboardLayout всегда была истинной и страница
 * уходила в бесконечную перезагрузку.
 *
 * Поэтому значение дублируется в localStorage: в iframe он работает
 * (там же лежит access token), а cookie остаётся основным источником —
 * поведение в Chrome/Firefox и на самом сайте не меняется.
 */

export const LANG_CHECKED_KEY = 'lang_checked';
export const LANG_KEY = 'lang';

const COOKIE_OPTIONS = {SameSite: 'None', secure: true, path: '/'};
const RELOAD_GUARD_KEY = `${PROJ_PFX}lang_reload_guard`;

// Хранилище может быть недоступно (приватный режим, заблокированное
// third-party storage) — тогда просто работаем на одних cookie.
function readStorage (storage: Storage, key: string): string | undefined {
  try {
    return storage.getItem(key) || undefined;
  } catch (error) {
    return undefined;
  }
}

function writeStorage (storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch (error) {
    console.warn('langStorage: storage is not writable', error);
  }
}

function removeStorage (storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch (error) {
    console.warn('langStorage: storage is not writable', error);
  }
}

/**
 * Чтение языка: cookie приоритетнее зеркала, поэтому уже сохранённые у
 * пользователей cookie продолжают работать как раньше.
 */
export function getLang (key: string): string | undefined {
  return Cookies.get(key) || readStorage(localStorage, PROJ_PFX + key);
}

/** Запись языка: cookie с прежними атрибутами + зеркало в localStorage. */
export function setLang (key: string, value: string): void {
  Cookies.set(key, value, COOKIE_OPTIONS);
  writeStorage(localStorage, PROJ_PFX + key, value);
}

export function getCheckedLang (): string | undefined {
  return getLang(LANG_CHECKED_KEY);
}

export function setCheckedLang (value: string): void {
  setLang(LANG_CHECKED_KEY, value);
}

/**
 * Разрешает перезагрузку ради применения языка не чаще одного раза на язык
 * и вкладку. Страховка на случай, когда недоступны и cookie, и localStorage:
 * без неё проверка языка снова зациклила бы перезагрузку.
 */
export function allowLangReload (lang: string): boolean {
  if (readStorage(sessionStorage, RELOAD_GUARD_KEY) === lang) {
    return false;
  }

  writeStorage(sessionStorage, RELOAD_GUARD_KEY, lang);

  return true;
}

/** Язык применился — снимаем защиту, чтобы следующая смена снова сработала. */
export function clearLangReloadGuard (): void {
  removeStorage(sessionStorage, RELOAD_GUARD_KEY);
}